import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchUserName, fetchPeriodHistory } from '../../lib/userDataService';
import LegalOverlay from '../LegalOverlay';

/**
 * CycleSettingsScreen — Cycle experience settings, editorial not utilitarian.
 *
 * Six sections:
 *   1. Account            — name, email, sign-out
 *   2. Cycle preferences  — average cycle length, period notifications
 *   3. Health Data        — export, hormone panels, connected sources
 *   4. Privacy            — data usage, deletion
 *   5. Support            — help, feedback, about
 *   6. Theo Personalization — memory scope, tone, "Theo has learned from X cycles"
 *
 * Design: premium minimal — grouped .cx-set-list rows, no Android-style
 * chevrons on every row. Chevron only on rows that navigate away.
 */

const LS = (userId, k) => `pcos_${userId}_settings_${k}`;
const loadBool = (userId, k, def = false) => {
  try { const v = localStorage.getItem(LS(userId, k)); return v == null ? def : v === 'true'; } catch { return def; }
};
const saveBool = (userId, k, v) => { try { localStorage.setItem(LS(userId, k), String(v)); } catch { /* storage unavailable */ } };
const loadStr = (userId, k, def = '') => {
  try { return localStorage.getItem(LS(userId, k)) ?? def; } catch { return def; }
};
const saveStr = (userId, k, v) => { try { localStorage.setItem(LS(userId, k), v); } catch { /* storage unavailable */ } };

export default function CycleSettingsScreen({ userId, cycleLength = 28 }) {
  const [profile, setProfile] = useState({ name: null, email: null, loading: true });
  const [cyclesTracked, setCyclesTracked] = useState(0);

  // Preferences — persisted locally (no schema change required)
  const [periodReminder, setPeriodReminder]   = useState(() => loadBool(userId, 'periodReminder', true));
  const [dailyCheckin,   setDailyCheckin]     = useState(() => loadBool(userId, 'dailyCheckin', true));
  const [insightsPush,   setInsightsPush]     = useState(() => loadBool(userId, 'insightsPush', true));
  const [reducedMotion,  setReducedMotion]    = useState(() => loadBool(userId, 'reducedMotion', false));
  const [legalOpen,      setLegalOpen]        = useState(null); // 'terms' | 'privacy' | null
  const [theoTone,       setTheoTone]         = useState(() => loadStr(userId, 'theoTone', 'warm'));
  const [memoryScope,    setMemoryScope]      = useState(() => loadStr(userId, 'memoryScope', '90d'));

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setProfile({ name: null, email: null, loading: false }); return; }
      const [name, { data: { user } }, periods] = await Promise.all([
        fetchUserName(userId),
        supabase.auth.getUser(),
        fetchPeriodHistory(userId),
      ]);
      if (!alive) return;
      setProfile({ name, email: user?.email || null, loading: false });
      setCyclesTracked(Array.isArray(periods) ? periods.length : 0);
    })();
    return () => { alive = false; };
  }, [userId]);

  const toggle = (setter, key) => (v) => {
    setter(v);
    saveBool(userId, key, v);
  };
  const choose = (setter, key) => (v) => {
    setter(v);
    saveStr(userId, key, v);
  };

  const memoryLine = cyclesTracked === 0
    ? 'Theo has just met your body.'
    : cyclesTracked === 1
      ? 'Theo has learned from 1 cycle so far.'
      : `Theo has learned from ${cyclesTracked} cycles so far.`;

  return (
    <div className="cx-sub-body" data-testid="cycle-settings-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">SETTINGS · CYCLE</div>
        <h1 className="cx-intel-h1">Make it <em>yours.</em></h1>
        <p className="cx-sentence" style={{ marginTop: 14, maxWidth: 340 }}>
          {memoryLine} Adjust what she notices and how she talks.
        </p>
      </section>

      {/* ── ACCOUNT ────────────────────────────────────────── */}
      <SettingsSection label="Account" tone="neutral">
        <ReadOnlyRow k="Name" v={profile.loading ? '—' : (profile.name || 'Not set')} testid="settings-name" />
        <ReadOnlyRow k="Email" v={profile.loading ? '—' : (profile.email || 'Not set')} testid="settings-email" />
        <ActionRow
          k="Sign out"
          hint="You will need to sign back in to access your cycle."
          onClick={async () => { await supabase.auth.signOut(); }}
          destructive
          testid="settings-signout"
        />
      </SettingsSection>

      {/* ── CYCLE ──────────────────────────────────────────── */}
      <SettingsSection label="Cycle preferences" tone="cycle">
        <ReadOnlyRow k="Average cycle length" v={`${cycleLength} days`} testid="settings-cycle-length" />
        <ToggleRow
          k="Period reminders"
          hint="Gentle nudge 2 days before your expected period."
          value={periodReminder}
          onChange={toggle(setPeriodReminder, 'periodReminder')}
          testid="settings-period-reminder"
        />
        <ToggleRow
          k="Daily check-in prompt"
          hint="A one-tap way to keep your body's memory current."
          value={dailyCheckin}
          onChange={toggle(setDailyCheckin, 'dailyCheckin')}
          testid="settings-daily-checkin"
        />
      </SettingsSection>

      {/* ── HEALTH DATA ────────────────────────────────────── */}
      <SettingsSection label="Health data" tone="health">
        <ReadOnlyRow k="Cycles tracked" v={String(cyclesTracked)} testid="settings-cycles-tracked" />
        <ActionRow
          k="Export my data"
          hint="Download a JSON copy of everything you've logged."
          disabled
          badge="Soon"
          badgeTone="soon"
          testid="settings-export"
        />
        <ActionRow
          k="Add hormone panel"
          hint="Upload a lab PDF from the Health tab."
          disabled
          badge="Health tab"
          badgeTone="health"
          testid="settings-hormones"
        />
      </SettingsSection>

      {/* ── PRIVACY ────────────────────────────────────────── */}
      <SettingsSection label="Privacy" tone="privacy">
        <ReadOnlyRow
          k="Storage"
          v="Encrypted · Supabase"
          testid="settings-storage"
        />
        <ReadOnlyRow
          k="Model access"
          v="Only your data, never shared"
          testid="settings-model"
        />
        <ActionRow
          k="Delete my account"
          hint="Permanent — cycles, symptoms, hormones, all of it."
          destructive
          disabled
          badge="Contact us"
          badgeTone="soon"
          testid="settings-delete"
        />
      </SettingsSection>

      {/* ── THEO PERSONALIZATION ───────────────────────────── */}
      <SettingsSection label="Theo — personalization" tone="theo">
        <div className="cx-set-theo-memory" data-testid="settings-theo-memory">
          <span className="cx-theo-tag">MEMORY</span>
          <span className="cx-set-theo-memory-text">{memoryLine}</span>
        </div>

        <ChoiceRow
          k="Tone"
          hint="How Theo speaks to you."
          options={[
            { key: 'warm',      label: 'Warm'     },
            { key: 'clinical',  label: 'Clinical' },
            { key: 'brief',     label: 'Brief'    },
          ]}
          value={theoTone}
          onChange={choose(setTheoTone, 'theoTone')}
          testid="settings-theo-tone"
        />

        <ChoiceRow
          k="Memory window"
          hint="How far back Theo looks for patterns."
          options={[
            { key: '30d', label: '30 days' },
            { key: '90d', label: '90 days' },
            { key: 'all', label: 'All'     },
          ]}
          value={memoryScope}
          onChange={choose(setMemoryScope, 'memoryScope')}
          testid="settings-memory-scope"
        />

        <ToggleRow
          k="Pattern notifications"
          hint="A quiet ping when Theo notices something meaningful."
          value={insightsPush}
          onChange={toggle(setInsightsPush, 'insightsPush')}
          testid="settings-insights-push"
        />

        <ToggleRow
          k="Reduced motion"
          hint="Softer spring animations across the cycle screen."
          value={reducedMotion}
          onChange={toggle(setReducedMotion, 'reducedMotion')}
          testid="settings-reduced-motion"
        />
      </SettingsSection>

      {/* ── SUPPORT ────────────────────────────────────────── */}
      <SettingsSection label="Support" tone="support">
        <ActionRow
          k="Help & FAQ"
          hint="Answers to the most-asked questions."
          onClick={() => window.open('https://app.pcosinsights.com/help', '_blank', 'noopener')}
          testid="settings-help"
        />
        <ActionRow
          k="Send feedback"
          hint="Tell us what you'd love to see next."
          onClick={() => window.open('mailto:hello@pcosinsights.com?subject=Feedback', '_blank')}
          testid="settings-feedback"
        />
        <ActionRow
          k="Terms of Service"
          hint="Rules of the road for using PCOS Insights."
          onClick={() => setLegalOpen('terms')}
          testid="settings-terms"
        />
        <ActionRow
          k="Privacy Policy"
          hint="How we collect, store and protect your health data."
          onClick={() => setLegalOpen('privacy')}
          testid="settings-privacy"
        />
        <ReadOnlyRow k="App version" v="1.0 · Cycle" testid="settings-version" />
      </SettingsSection>

      <section className="cx-intel-section">
        <p className="cx-fine-print">
          Settings are saved to this device. Cross-device sync coming soon.
        </p>
      </section>
      {legalOpen && <LegalOverlay kind={legalOpen} onClose={() => setLegalOpen(null)} />}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   ATOMS
   ═════════════════════════════════════════════════════════════════ */

function SettingsSection({ label, tone = 'neutral', children }) {
  return (
    <section className="cx-intel-section" data-tone={tone}>
      <div className="cx-intel-label">{label}</div>
      <div className="cx-set-list">{children}</div>
    </section>
  );
}

function ReadOnlyRow({ k, v, testid }) {
  return (
    <div className="cx-set-row cx-set-row--read" data-testid={testid}>
      <div className="cx-set-row-l">
        <div className="cx-set-row-k">{k}</div>
      </div>
      <div className="cx-set-row-r">
        <span className="cx-set-row-v">{v}</span>
      </div>
    </div>
  );
}

function ToggleRow({ k, hint, value, onChange, testid }) {
  return (
    <div className="cx-set-row" data-testid={testid}>
      <div className="cx-set-row-l">
        <div className="cx-set-row-k">{k}</div>
        {hint && <div className="cx-set-row-hint">{hint}</div>}
      </div>
      <div className="cx-set-row-r">
        <button
          role="switch"
          aria-checked={value}
          className={`cx-toggle ${value ? 'cx-on' : ''}`}
          onClick={() => onChange(!value)}
          data-testid={`${testid}-switch`}
        >
          <span className="cx-toggle-thumb" />
        </button>
      </div>
    </div>
  );
}

function ChoiceRow({ k, hint, options, value, onChange, testid }) {
  return (
    <div className="cx-set-row cx-set-row--stack" data-testid={testid}>
      <div className="cx-set-row-l">
        <div className="cx-set-row-k">{k}</div>
        {hint && <div className="cx-set-row-hint">{hint}</div>}
      </div>
      <div className="cx-set-choice">
        {options.map(o => (
          <button
            key={o.key}
            className={`cx-set-choice-pill ${value === o.key ? 'cx-on' : ''}`}
            onClick={() => onChange(o.key)}
            data-testid={`${testid}-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ActionRow({ k, hint, onClick, destructive, disabled, badge, badgeTone, testid }) {
  return (
    <button
      className={`cx-set-row cx-set-row--action ${destructive ? 'cx-set-row--danger' : ''} ${disabled ? 'cx-set-row--disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
    >
      <div className="cx-set-row-l">
        <div className="cx-set-row-k">{k}</div>
        {hint && <div className="cx-set-row-hint">{hint}</div>}
      </div>
      <div className="cx-set-row-r">
        {badge && <span className={`cx-set-row-badge${badgeTone ? ' cx-set-row-badge--' + badgeTone : ''}`}>{badge}</span>}
        {!disabled && (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="m9 6 6 6-6 6"/>
          </svg>
        )}
      </div>
    </button>
  );
}
